import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Mail, Lock, Shield, ArrowLeft } from 'lucide-react'
import { useAuth, type MFARequiredError } from '@/contexts/AuthContext'
import { Button, Input, Card } from '@/components/ui'
import Logo from '@/components/shared/Logo'
import type { MultiFactorResolver } from 'firebase/auth'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
})

type LoginForm = z.infer<typeof loginSchema>

export default function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('rememberMe') === 'true')
  const [isLoading, setIsLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaError, setMfaError] = useState<string | null>(null)
  const { signIn, signInWithMFA } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: localStorage.getItem('rememberMe') === 'true' ? (sessionStorage.getItem('rememberedEmail') || '') : '',
    },
  })

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    setLoginError(null)
    try {
      if (rememberMe) {
        sessionStorage.setItem('rememberedEmail', data.email)
      } else {
        sessionStorage.removeItem('rememberedEmail')
      }
      const userData = await signIn(data.email, data.password, rememberMe)
      // Family members go to the Family Portal, not the main dashboard
      const destination = userData.role === 'family' ? '/family' : from
      navigate(destination, { replace: true })
    } catch (err: unknown) {
      const error = err as { code?: string }

      // Check if MFA is required
      if (error.code === 'auth/multi-factor-auth-required') {
        const mfaError = err as MFARequiredError
        setMfaResolver(mfaError.resolver)
        setMfaRequired(true)
        setIsLoading(false)
        return
      }

      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setLoginError('Invalid email or password')
      } else if (error.code === 'auth/too-many-requests') {
        setLoginError('Too many failed attempts. Please try again later.')
      } else {
        setLoginError('Failed to sign in. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleMfaSubmit = async () => {
    if (!mfaResolver || mfaCode.length !== 6) {
      setMfaError('Please enter a valid 6-digit code')
      return
    }

    setIsLoading(true)
    setMfaError(null)

    try {
      const userData = await signInWithMFA(mfaResolver, mfaCode)
      const destination = userData.role === 'family' ? '/family' : from
      navigate(destination, { replace: true })
    } catch (err: unknown) {
      const error = err as { code?: string }
      if (error.code === 'auth/invalid-verification-code') {
        setMfaError('Invalid verification code. Please try again.')
      } else {
        setMfaError('Failed to verify code. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setMfaRequired(false)
    setMfaResolver(null)
    setMfaCode('')
    setMfaError(null)
  }

  // MFA verification view
  if (mfaRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gray-50 dark:bg-gray-900">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="flex justify-center mb-4">
              <Logo size="xl" />
            </Link>
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Two-Factor Authentication</h1>
            <p className="text-gray-600 dark:text-gray-400">Enter the code from your authenticator app</p>
          </div>

          {/* MFA Form */}
          <Card padding="lg" theme="light">
            <div className="space-y-5">
              <Input
                theme="light"
                label="Verification Code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                error={mfaError || undefined}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                autoFocus
              />

              <Button
                onClick={handleMfaSubmit}
                isLoading={isLoading}
                disabled={mfaCode.length !== 6}
                className="w-full"
              >
                Verify & Sign In
              </Button>

              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to login
              </button>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gray-50 dark:bg-gray-900">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="flex justify-center mb-4">
            <Logo size="xl" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Welcome back</h1>
          <p className="text-gray-600 dark:text-gray-400">Sign in to your account</p>
        </div>

        {/* Form */}
        <Card padding="lg" theme="light">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {loginError && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 px-4 py-3 text-sm text-red-700 dark:text-red-300" role="alert">
                {loginError}
              </div>
            )}
            <Input
              theme="light"
              label="Email"
              type="email"
              placeholder="you@agency.com"
              leftIcon={<Mail className="h-5 w-5" />}
              error={errors.email?.message}
              {...register('email')}
            />

            <div>
              <Input
                theme="light"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                leftIcon={<Lock className="h-5 w-5" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                }
                error={errors.password?.message}
                {...register('password')}
              />
              <div className="mt-2 flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => {
                      setRememberMe(e.target.checked)
                      localStorage.setItem('rememberMe', String(e.target.checked))
                    }}
                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 accent-primary-600"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Remember me</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button type="submit" isLoading={isLoading} className="w-full">
              Sign in
            </Button>
          </form>
        </Card>

        {/* Sign up link */}
        <p className="mt-6 text-center text-gray-600 dark:text-gray-400">
          Don't have an account?{' '}
          <Link to="/signup" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
            Start 30-Day Free Trial
          </Link>
        </p>
      </div>
    </div>
  )
}
